import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@quotech/auth'
import { templateApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { TemplateStatus, ChecklistTemplate } from '@/types/qa'
import {
  Plus,
  Search,
  FileText,
  ClipboardList,
  Copy,
  MoreVertical,
  Archive,
  Edit2,
  CheckCircle,
  RefreshCw,
  X,
  Globe2,
  Building2,
  AlertTriangle,
} from 'lucide-react'

export default function TemplatesPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeTab, setActiveTab] = useState<'library' | 'organisation'>('library')
  const [statusFilter, setStatusFilter] = useState<TemplateStatus | 'all'>('all')
  const [tradeFilter, setTradeFilter] = useState('')
  const queryClient = useQueryClient()

  // Wait for org context before fetching
  const organisationId = useAuthStore((s) => s.organisationId)
  const isAuthReady = !!organisationId

  const { data: templates, isLoading, error, refetch } = useQuery({
    queryKey: ['qa-templates', organisationId],
    queryFn: () => templateApi.getTemplates(),
    enabled: isAuthReady,
  })
  
  const showLoading = isLoading || !isAuthReady

  const filteredTemplates = (Array.isArray(templates) ? templates : []).filter((template: ChecklistTemplate) => {
    if (activeTab === 'library' ? template.organisation !== null : template.organisation === null) return false
    if (statusFilter !== 'all' && template.status !== statusFilter) return false
    if (tradeFilter && template.trade !== tradeFilter) return false
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      template.name?.toLowerCase().includes(query) ||
      template.trade?.toLowerCase().includes(query) ||
      template.system?.toLowerCase().includes(query) ||
      template.tags?.some((tag) => tag.toLowerCase().includes(query))
    )
  })

  const forkMutation = useMutation({
    mutationFn: (template: ChecklistTemplate) => templateApi.forkTemplate(template.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-templates'] })
      setActiveTab('organisation')
    },
  })
  const trades = [...new Set((templates || []).map((template) => template.trade).filter(Boolean))].sort()

  const getStatusIcon = (status: TemplateStatus) => {
    switch (status) {
      case 'published':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />
      case 'archived':
        return <Archive className="w-4 h-4 text-muted-foreground" />
      default:
        return <FileText className="w-4 h-4 text-muted-foreground" />
    }
  }

  const getStatusStyles = (status: TemplateStatus) => {
    switch (status) {
      case 'published':
        return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
      case 'archived':
        return 'bg-muted text-muted-foreground'
      default:
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Templates</h1>
          <p className="text-muted-foreground">Manage QA checklist templates</p>
        </div>
        <Link
          to="/templates/new"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-600 dark:hover:bg-primary-400 shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
        >
          <Plus className="w-4 h-4" />
          New Template
        </Link>
      </div>

      <div className="flex gap-2 border-b border-border" role="tablist" aria-label="Template libraries">
        <Tab active={activeTab === 'library'} onClick={() => setActiveTab('library')} icon={Globe2} label="Quotech Library" />
        <Tab active={activeTab === 'organisation'} onClick={() => setActiveTab('organisation')} icon={Building2} label="Organisation templates" />
      </div>

      <div className="bg-card rounded-xl shadow-sm border border-border p-4">
        <div className="flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-background border border-input rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <select value={tradeFilter} onChange={(event) => setTradeFilter(event.target.value)} className="rounded-xl border border-input bg-background px-3 py-2.5 text-sm">
            <option value="">All trades</option>{trades.map((trade) => <option key={trade} value={trade}>{trade}</option>)}
          </select>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as TemplateStatus | 'all')} className="rounded-xl border border-input bg-background px-3 py-2.5 text-sm">
            <option value="all">All lifecycle states</option><option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
          </select>
          <button
            onClick={() => refetch()}
            className="p-2.5 rounded-xl border border-input text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Templates grid */}
      {showLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-40 bg-card rounded-xl border border-border animate-pulse" />
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-16 bg-card rounded-xl border border-border">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 mx-auto mb-4 flex items-center justify-center">
            <ClipboardList className="w-8 h-8 text-destructive" />
          </div>
          <p className="font-medium text-foreground">Error loading templates</p>
          <p className="text-sm text-muted-foreground mt-1">Please try again later</p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : filteredTemplates?.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template: ChecklistTemplate) => (
            <div
              key={template.id}
              className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 hover:shadow-md transition-all group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center',
                  template.status === 'published' 
                    ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                    : 'bg-muted'
                )}>
                  {getStatusIcon(template.status)}
                </div>
                <div className="relative">
                  <button className="p-1.5 hover:bg-accent rounded-lg transition-colors">
                    <MoreVertical className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              </div>
              <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                {template.name}
              </h3>
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{template.system || 'Uncategorised system'}</p>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={cn('text-xs font-medium px-2 py-1 rounded-full', getStatusStyles(template.status))}>
                    {template.status}
                  </span>
                  {template.trade && (
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      {template.trade}
                    </span>
                  )}
                  {template.organisation === null && <span className="text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">Read-only global</span>}
                </div>
                <span className="text-xs text-muted-foreground font-medium">
                  v{template.version}
                </span>
              </div>
              <div className="pt-3 border-t border-border flex items-center gap-2">
                <Link
                  to={`/templates/${template.id}`}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  {template.organisation === null || template.status !== 'draft' ? 'View' : 'Edit'}
                </Link>
                {template.source_scope === 'global' && template.status === 'published' && <button onClick={() => forkMutation.mutate(template)} disabled={forkMutation.isPending}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50">
                  <Copy className="w-3.5 h-3.5" />Install / fork
                </button>}
              </div>
              {template.source_scope === 'organisation' && <p className="mt-2 text-[11px] text-muted-foreground">
                {template.source_template
                  ? `Source ${template.source_template} · source version ${template.source_version ?? 'unknown'}`
                  : 'Organisation-owned source'}
                {template.update_available && <span className="ml-1 font-medium text-amber-700">· Update available</span>}
              </p>}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-card rounded-xl border border-border">
          <div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center">
            <ClipboardList className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="font-medium text-foreground">No templates found</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            {searchQuery
              ? 'Try adjusting your search'
              : 'Create your first template to get started'}
          </p>
          {!searchQuery && (
            <Link
              to="/templates/new"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Template
            </Link>
          )}
        </div>
      )}
      {forkMutation.isError && <div role="alert" className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive"><AlertTriangle className="h-4 w-4" />The backend could not create an organisation fork from this published template.</div>}
    </div>
  )
}

function Tab({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: typeof Globe2; label: string }) {
  return <button type="button" role="tab" aria-selected={active} onClick={onClick} className={cn('inline-flex min-h-11 items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium', active ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground')}><Icon className="h-4 w-4" />{label}</button>
}
